import { useEffect, useMemo, useState } from "react"

import { App, Checkbox, Flex, Form, Skeleton, Space, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import ButtonComponent from "../../../../../Components/Button"
import PrivacyPolicy from "../../../../../Components/PrivacyPolicy"
import SelectComponent from "../../../../../Components/Select"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { modalConfig } from "../../../../../theme"
import utils from "../../../../../Utils/utils"
import {
  validateArnCode,
  validateBinCode,
  validateCaidCode
} from "../../../ConfigureProfilePage/utils/codeValidators"
import {
  clearActivationError,
  selectActivation,
  selectActivationDescriptors,
  selectActivationPrefill
} from "../../redux/business.slice"
import {
  activate,
  getActivationDescriptors,
  getActivationPrefill
} from "../../redux/business.thunks"

import styles from "./activationModal.module.less"

const { Title, Text, Link } = Typography

// Matches BillingDescriptorsFormConfigure's tooltip icon.
const TOOLTIP_ICON_STYLE = { fontSize: 16, color: "#7A7A83" }

const upper = (value) => String(value).toUpperCase()

// The chip the onboarding Alerts form renders under its descriptor input
// (Components/Forms/BillingDescriptorsForm). Copied rather than imported: there
// it is a private component inside a 25KB legacy form, and exporting it would
// mean editing that file for presentation alone.
const FilterTag = ({ tag, closable, onDelete, theme, testIdPrefix }) => (
  <Tag
    bordered={theme === "dark"}
    color="processing"
    closable={closable}
    onClose={onDelete}
    data-testid={testIdPrefix ? `${testIdPrefix}-chip` : undefined}
    data-value={tag}
    closeIcon={
      <Icon
        type="cross_small"
        style={{ fontSize: 20, lineHeight: 1, color: "#8aa7f4" }}
        data-testid={testIdPrefix ? `${testIdPrefix}-chip-remove` : undefined}
        data-value={tag}
      />
    }
    style={{
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 6,
      padding: "0px 4px 0px 10px",
      margin: 0,
      textWrap: "balance",
      display: "flex",
      alignItems: "center",
      color: "#1448CB",
      background: theme === "dark" ? "none" : "#F2F6FF"
    }}
  >
    {tag}
  </Tag>
)

// Receipt activation flow (BLOCK 5 of CHA1-1967). Full-screen modal with
// billing-descriptor tags plus, for Order Insight, either 4 ARN codes or a
// BIN+CAID pair. Continue POSTs to /business/activation/ - success closes the
// modal and refetches programs + descriptors (§14).
export const ActivationModal = ({ program, onClose, businessAccountId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { modal } = App.useApp()
  const isMobile = useSelector((s) => !!s.nav?.isMobile)
  const theme = useSelector((s) => s.nav?.theme)
  const { saving, error: activationError } = useSelector(selectActivation)
  const {
    rows: fetchedDescriptors,
    program: fetchedProgram,
    fetching: descriptorsFetching
  } = useSelector(selectActivationDescriptors)
  const prefill = useSelector(selectActivationPrefill)

  const isOI = program?.program === "oi"

  // Existing descriptors are locked (non-deletable) chips - the merchant may
  // only add new ones through this flow, never remove one that already exists.
  // Scoped to the program on screen: rows fetched for the program just left are
  // not locked chips here, so they would land in `tags` as newly added
  // descriptors and be submitted on Continue.
  const locked = useMemo(
    () =>
      fetchedProgram && fetchedProgram === program?.program
        ? (fetchedDescriptors || []).map((d) => d.billing_descriptor).filter(Boolean)
        : [],
    [fetchedDescriptors, fetchedProgram, program?.program]
  )
  const lockedSet = useMemo(() => new Set(locked.map((s) => s.toUpperCase())), [locked])

  const [tags, setTags] = useState([])
  const [useBinCaid, setUseBinCaid] = useState(false)
  const [arn, setArn] = useState([])
  const [bin, setBin] = useState([])
  const [caid, setCaid] = useState([])
  const [errors, setErrors] = useState({})

  // Redux-owned activation error persists between mounts — clear it so the
  // freshly-opened modal never shows a stale message from the previous attempt.
  // Descriptors are fetched here (not derived from the page-level list) so the
  // locked chips reflect ALL existing descriptors, not the filtered view.
  useEffect(() => {
    dispatch(clearActivationError())

    // Everything typed so far belongs to the program it was typed under. The
    // modal is not remounted when `program` changes, so without this the tags
    // of the program just left survive into the next one — and they are not
    // locked chips there, they are new descriptors this form would submit.
    setTags([])
    setArn([])
    setBin([])
    setCaid([])
    setUseBinCaid(false)
    setErrors({})

    if (program?.program)
      dispatch(getActivationDescriptors({ program: program.program, businessAccountId }))
    if (program?.program === "oi") dispatch(getActivationPrefill({ businessAccountId }))
  }, [dispatch, program?.program, businessAccountId])

  // Seed the code fields from what the account already holds (§11.9, CHA1-2306).
  // Runs on `loaded` rather than on the arrays: an account with no codes yet
  // answers with three empty lists, and keying off the values would re-run on
  // every render and wipe what the merchant is typing.
  useEffect(() => {
    if (!isOI || !prefill.loaded) return
    setArn(prefill.arnCodes)
    setBin(prefill.binCodes)
    setCaid(prefill.caidCodes)
    // The merchant's stored opt-in wins: `display_mode` answers "which code
    // sets does the account hold", and an account holding both reports BOTH —
    // read as the mode to open in, that silently un-ticked the box of every
    // merchant who added BIN+CAID on top of existing ARNs. `useBinCaid` is the
    // choice itself, kept exactly as the alerts form keeps it (§11.9,
    // BusinessAccount.using_bin_caid). It is null only for accounts that
    // predate the field, which still infer the mode from what they hold.
    //
    // Either way one variant opens, not both: §11.9 shows both blocks at once
    // in the wizard, but this form submits one per request (§8.2) - showing
    // both with no way to say which is being submitted would send a set the
    // merchant never chose. The other block stays one checkbox away, prefilled.
    setUseBinCaid(prefill.useBinCaid ?? prefill.displayMode === "BIN_CAID")
    // Deliberately keyed on `loaded` alone. It is reset to false on every fetch,
    // so this fires exactly once per response and reads the values as they land.
    // Listing the arrays instead would re-seed mid-edit on any unrelated render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOI, prefill.loaded])

  // Sync locked chips into tags once they load — the async fetch resolves
  // after mount, so a lazy initializer wouldn't see them.
  useEffect(() => {
    setTags((prev) => {
      const kept = new Set(prev.filter((v) => !lockedSet.has(String(v).toUpperCase())))
      for (const l of locked) kept.add(l)
      return [...kept]
    })
  }, [locked, lockedSet])

  // What the fields were seeded with, so "changed" means changed by the merchant
  // rather than merely filled. Empty for Consumer Clarity, which has no code
  // fields and never fetches a prefill — the codes held here would be the ones
  // fetched for Order Insight, and comparing against those would report a CC
  // form as edited the moment it opened.
  //
  // Deliberately not gated on `prefill.loaded`: a refetch flips that to false
  // while keeping the values it already holds, so gating on it would blank the
  // locked set for the length of the request — on every reopen the chips would
  // show their × again until the response landed, and `dirty` would fire the
  // exit confirm on a form nobody touched.
  const seeded = useMemo(
    () =>
      isOI
        ? { arn: prefill.arnCodes, bin: prefill.binCodes, caid: prefill.caidCodes }
        : { arn: [], bin: [], caid: [] },
    [isOI, prefill.arnCodes, prefill.binCodes, prefill.caidCodes]
  )

  // Codes the account already holds are locked chips, exactly like the existing
  // billing descriptors above (CHA1-2746): this flow only adds codes, it never
  // removes one that was already saved. Same rule the alerts form applies to a
  // merchant's prefilled ARN codes (Components/Forms/BillingDescriptorsForm).
  const lockedCodes = useMemo(
    () => ({
      arn: new Set(seeded.arn.map(upper)),
      bin: new Set(seeded.bin.map(upper)),
      caid: new Set(seeded.caid.map(upper))
    }),
    [seeded]
  )

  if (!program) return null

  const newTags = tags.filter((tag) => !lockedSet.has(tag.toUpperCase()))
  // The codes arrive in their own request, separate from the descriptors. Left
  // on `descriptorsFetching` alone the code fields unlock as soon as the
  // descriptors land, so a slower prefill would overwrite whatever the merchant
  // had already typed into them.
  const codesLoading = descriptorsFetching || (isOI && prefill.fetching)

  const changedFrom = (current, original) =>
    current.length !== original.length || current.some((value, i) => value !== original[i])

  const dirty =
    newTags.length > 0 ||
    changedFrom(arn, seeded.arn) ||
    changedFrom(bin, seeded.bin) ||
    changedFrom(caid, seeded.caid)

  const handleTagsChange = (value) => {
    // Keep locked tags no matter what the Select emits - Antd removes on
    // backspace even when we style the × away, so guard it here too.
    const kept = new Set(value.map((s) => s.trim()).filter(Boolean))
    for (const l of locked) kept.add(l)
    setTags([...kept])
  }

  // Removal now happens on the chip below the field, not inside the input.
  const handleRemoveTag = (tag) => {
    if (lockedSet.has(String(tag).toUpperCase())) return
    setTags((prev) => prev.filter((value) => value !== tag))
  }

  // Keep the saved codes no matter what the Select emits — antd removes the
  // last tag on backspace even though the chips are rendered outside the input,
  // so the guard has to live here too, not only on the chip's × (handleTagsChange
  // does the same for descriptors). Restored codes go back to the front, where
  // the prefill put them.
  const handleCodesChange = (setValues, lockedValues, normalize) => (values) => {
    const next = normalize ? values.map(normalize) : values
    const restored = lockedValues.filter(
      (code) => !next.some((value) => upper(value) === upper(code))
    )
    setValues([...restored, ...next])
  }

  // Every field here hides its own tags, so each renders its values as chips
  // underneath instead.
  const renderChips = (values, setValues, lockedValues, field) =>
    values.length ? (
      <Flex gap={8} wrap="wrap" className={styles.chips}>
        {values.map((value) => (
          <FilterTag
            key={value}
            tag={value}
            theme={theme}
            closable={!lockedValues.has(upper(value))}
            onDelete={() => setValues(values.filter((item) => item !== value))}
            testIdPrefix={`deflect-activation-${field}`}
          />
        ))}
      </Flex>
    ) : null

  const validateTags = () => {
    if (newTags.length === 0 && locked.length === 0) {
      return t("This field is required.")
    }
    return null
  }

  // Four is a minimum, not a quota (CHA1-2763). It is the rule the rest of the
  // platform applies - the onboarding alerts form rejects fewer than four and
  // accepts any number above (Components/Forms/BillingDescriptorsFormConfigure),
  // and this modal's own tooltip already says "at least 4 ARN codes per
  // descriptor". Uniqueness therefore compares against the list's own length,
  // not against 4, or a fifth valid code would read as a duplicate.
  const validateArnList = () => {
    if (!arn.length) return t("This field is required.")
    if (arn.length < 4) return t("Enter at least 4 different ARN codes.")
    if (new Set(arn).size !== arn.length) return t("ARN codes must be different.")
    for (const c of arn) {
      const err = validateArnCode(c)
      if (err) return t(err)
    }
    return null
  }

  const validateBinList = () => {
    if (!bin.length) return t("This field is required.")
    for (const c of bin) {
      const err = validateBinCode(c)
      if (err) return t(err)
    }
    return null
  }

  const validateCaidList = () => {
    if (!caid.length) return t("This field is required.")
    for (const c of caid) {
      const err = validateCaidCode(c)
      if (err) return t(err)
    }
    return null
  }

  const handleContinue = async () => {
    const next = {}
    const tErr = validateTags()
    if (tErr) next.tags = tErr

    if (isOI) {
      if (useBinCaid) {
        const bErr = validateBinList()
        if (bErr) next.bin = bErr
        const cErr = validateCaidList()
        if (cErr) next.caid = cErr
      } else {
        const aErr = validateArnList()
        if (aErr) next.arn = aErr
      }
    }

    setErrors(next)
    if (Object.keys(next).length) return

    // Send the full descriptor set (locked + new), not just newTags — the
    // backend requires a non-empty list, and a merchant on OI who only added
    // ARN/BIN/CAID would otherwise submit an empty descriptors array.
    const payload = { program: program.program, billing_descriptors: tags }
    if (isOI) {
      if (useBinCaid) {
        payload.submission_mode = "BIN_CAID"
        payload.bin_codes = bin
        payload.caid_codes = caid
      } else {
        payload.submission_mode = "ARN"
        payload.arn_codes = arn
      }
    }

    const result = await dispatch(activate({ ...payload, businessAccountId }))
    if (result.meta.requestStatus === "fulfilled") onClose()
  }

  // The platform's shared confirm dialog (theme.js modalConfig) — the same one
  // onboarding uses when leaving a half-filled step (CHA1-2301).
  const handleClose = () => {
    if (!dirty) {
      onClose()
      return
    }

    const confirmConfig = modalConfig("exclamation")

    modal.confirm({
      ...confirmConfig,
      centered: !isMobile,

      title: t("Are you sure you want to exit?"),
      content: t("Any changes you have made will not be saved."),

      cancelText: t("Stay"),
      okText: t("Exit"),
      // Merged, not replaced — modalConfig sets `size: "large"` on both.
      cancelButtonProps: {
        ...confirmConfig.cancelButtonProps,
        "data-testid": "deflect-activation-discard-stay-btn"
      },
      okButtonProps: {
        ...confirmConfig.okButtonProps,
        "data-testid": "deflect-activation-discard-exit-btn"
      },
      onOk() {
        onClose()
      }
    })
  }

  // antd renders `help` inside .ant-form-item-explain-error; wrapping it gives
  // that message a locator of its own instead of a CSS-class fallback.
  const fieldError = (field, message) =>
    message ? (
      <span data-testid={`deflect-activation-${field}-error`} data-field={field}>
        {message}
      </span>
    ) : null

  const arnLabel = t("4 different VISA ARN codes (for Visa transactions)")
  const arnPlaceholder = t("Separate VISA ARN codes with commas")
  const binPlaceholder = t("Separate VISA BIN codes with commas")
  const caidPlaceholder = t("Separate VISA CAID codes with commas")

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      data-testid="deflect-activation-modal"
    >
      {/* Same header the onboarding steps use (Components/LoginHeader): the
          themed logo centred, close button pinned right. */}
      <div className={styles.header}>
        <img
          src={Images[theme === "dark" ? "logoWithTextWhite" : "logoWithText"]}
          alt="Chargeback"
        />
        <ButtonComponent
          size="large"
          icon={<Icon type="close_modal" />}
          style={{ position: "absolute", right: 32 }}
          onClick={handleClose}
          className="flex center"
          data-testid="deflect-activation-close-btn"
        />
      </div>

      <div className={styles.body}>
        <div className={styles.content}>
          {/* Title left, "Learn more" right — the same header row as the
              onboarding Alerts step (OnboardingAlertsPage). */}
          <Flex
            gap={16}
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
            wrap="wrap"
          >
            <Space direction="vertical" size={0}>
              <Title level={5} style={{ textAlign: "left" }}>
                {t("Receipt activation")}
              </Title>
              <Text type="secondary">
                {t("To activate Receipt, submit your billing descriptor.")}
              </Text>
            </Space>

            <Flex gap={12}>
              <ButtonComponent
                className="flex center small-space"
                block
                onClick={() =>
                  utils.goLink(
                    "https://help.chargeback.io/en/collections/16516790-setting-up-enrollments",
                    "_blank"
                  )
                }
                style={{ padding: "4px 20px" }}
              >
                {t("Learn more")}
                <Icon type="edit" style={{ fontSize: 20, color: "#7A7A83" }} />
              </ButtonComponent>
            </Flex>
          </Flex>

          {/* The form keeps the width it has on the onboarding step (523px), so
              widening the page to 1040 spaces out the header row without
              stretching the inputs across it. */}
          {/* antd Form purely for presentation: it supplies the required mark,
              the tooltip icon and the error slot exactly as
              BillingDescriptorsFormConfigure does. Values stay in local state,
              so no Form.Item carries a `name`. */}
          <Form
            className={cls("with-default-error", styles.formPane)}
            layout="vertical"
            requiredMark
          >
            <Form.Item
              label={t("Billing descriptor")}
              required
              tooltip={{
                title: t(
                  "The billing descriptor is the text you set in your payment processor. It appears on your customer's statement to identify each purchase. For merchants using Shopify, the Chargeback platform automatically adds the SP prefix to the billing descriptor."
                ),
                icon: (
                  <div>
                    <Icon type="circle_info" style={TOOLTIP_ICON_STYLE} />
                  </div>
                ),
                overlayStyle: { minWidth: 248 }
              }}
              validateStatus={errors.tags ? "error" : ""}
              help={fieldError("descriptor", errors.tags)}
            >
              {descriptorsFetching ? (
                <Skeleton.Input active block size="large" />
              ) : (
                <>
                  {/* Reads as a plain text input rather than a dropdown
                    (CHA1-2301): no arrow, the menu never opens, and chips are
                    rendered below instead of inside. */}
                  <SelectComponent
                    mode="tags"
                    size="large"
                    value={tags}
                    open={false}
                    suffixIcon={null}
                    tagRender={() => null}
                    tokenSeparators={[","]}
                    placeholder={t("Separate descriptor with commas")}
                    onChange={handleTagsChange}
                    className={cls("hidden-tags like-input", {
                      "has-value_descriptor": !!tags.length
                    })}
                    id="deflect-activation-descriptor-input"
                    data-testid="deflect-activation-descriptor-select"
                  />
                  {tags.length ? (
                    <Flex gap={8} wrap="wrap" className={styles.chips}>
                      {tags.map((tag) => (
                        <FilterTag
                          key={tag}
                          tag={tag}
                          theme={theme}
                          closable={!lockedSet.has(tag.toUpperCase())}
                          onDelete={() => handleRemoveTag(tag)}
                          testIdPrefix="deflect-activation-descriptor"
                        />
                      ))}
                    </Flex>
                  ) : null}
                </>
              )}
            </Form.Item>

            {isOI ? (
              <>
                {!useBinCaid ? (
                  <Form.Item
                    label={arnLabel}
                    required
                    tooltip={{
                      title: t(
                        "ARN codes are required for RDR enrollment. Enter at least 4 ARN codes per descriptor you wish to enroll for RDR. Only ARN codes starting with 2 or 7 are valid."
                      ),
                      icon: (
                        <div>
                          <Icon type="circle_info" style={TOOLTIP_ICON_STYLE} />
                        </div>
                      ),
                      overlayStyle: { minWidth: 248 }
                    }}
                    validateStatus={errors.arn ? "error" : ""}
                    help={fieldError("arn", errors.arn)}
                  >
                    <SelectComponent
                      mode="tags"
                      size="large"
                      value={arn}
                      open={false}
                      suffixIcon={null}
                      tagRender={() => null}
                      tokenSeparators={[","]}
                      placeholder={arnPlaceholder}
                      onChange={handleCodesChange(setArn, seeded.arn)}
                      disabled={codesLoading}
                      className={cls("hidden-tags like-input", {
                        "has-value_arn": !!arn.length
                      })}
                      id="deflect-activation-arn-input"
                      data-testid="deflect-activation-arn-select"
                    />
                    {renderChips(arn, setArn, lockedCodes.arn, "arn")}
                  </Form.Item>
                ) : null}

                {/* Boxed, like the configure form's BIN/CAID opt-in. */}
                <Form.Item style={{ border: "1px solid #E1E1E2", borderRadius: 8, padding: 12 }}>
                  <Checkbox
                    checked={useBinCaid}
                    onChange={(e) => setUseBinCaid(e.target.checked)}
                    disabled={codesLoading}
                    data-testid="deflect-activation-bin-caid-checkbox"
                  >
                    <Text strong>{t("I want to use BIN and CAID instead of ARNs.")}</Text>
                  </Checkbox>
                  <Text
                    type="secondary"
                    strong
                    style={{ display: "block", marginLeft: 26, marginTop: 4 }}
                  >
                    {t("Select this if you don't have any ARN codes.")}
                    <Link
                      href="https://help.chargeback.io/en/articles/12782785-how-to-get-bins-caid-for-rdr-alerts"
                      target="_blank"
                    >
                      {" "}
                      {t("Learn how to get your BIN and CAID")}
                    </Link>
                  </Text>
                </Form.Item>

                {useBinCaid ? (
                  <>
                    <Form.Item
                      label={t("BIN code")}
                      required
                      validateStatus={errors.bin ? "error" : ""}
                      help={fieldError("bin", errors.bin)}
                    >
                      <SelectComponent
                        mode="tags"
                        size="large"
                        value={bin}
                        open={false}
                        suffixIcon={null}
                        tagRender={() => null}
                        tokenSeparators={[","]}
                        placeholder={binPlaceholder}
                        onChange={handleCodesChange(setBin, seeded.bin)}
                        disabled={codesLoading}
                        className="hidden-tags like-input"
                        id="deflect-activation-bin-input"
                        data-testid="deflect-activation-bin-select"
                      />
                      {renderChips(bin, setBin, lockedCodes.bin, "bin")}
                    </Form.Item>
                    <Form.Item
                      label={t("CAID code")}
                      required
                      validateStatus={errors.caid ? "error" : ""}
                      help={fieldError("caid", errors.caid)}
                    >
                      <SelectComponent
                        mode="tags"
                        size="large"
                        value={caid}
                        open={false}
                        suffixIcon={null}
                        tagRender={() => null}
                        tokenSeparators={[","]}
                        placeholder={caidPlaceholder}
                        onChange={handleCodesChange(setCaid, seeded.caid, upper)}
                        disabled={codesLoading}
                        className="hidden-tags like-input"
                        id="deflect-activation-caid-input"
                        data-testid="deflect-activation-caid-select"
                      />
                      {renderChips(caid, setCaid, lockedCodes.caid, "caid")}
                    </Form.Item>
                  </>
                ) : null}
              </>
            ) : null}

            {activationError ? <div className={styles.error}>{activationError}</div> : null}
          </Form>

          {/* Directly under the form, 320px wide and centred — as in
              OnboardingAlertsPage. */}
          <Flex justify="center" style={{ width: "100%" }}>
            <ButtonComponent
              type="primary"
              size="large"
              loading={saving}
              disabled={codesLoading}
              onClick={handleContinue}
              className="flex center"
              style={{ width: 320, marginTop: "auto" }}
              data-testid="deflect-activation-continue-btn"
            >
              {t("Continue")}
            </ButtonComponent>
          </Flex>
        </div>
      </div>

      <div className={styles.footer}>
        <PrivacyPolicy />
      </div>
    </div>
  )
}
